import { makeStyles, mergeClasses } from '@fluentui/react-components';
import { SanitizeHtmlHelper } from '@syncfusion/ej2-base/src/sanitize-helper';
import React, { useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef } from 'react';
import { useFormView } from '../../../contexts/useFormView';
import { applyFormZoomSettings } from '../../../utils/FormZoomSettings';
import { HtmlMagnification, IHtmlViewerRef } from './HtmlMagnification';

export interface IHtmlViewerFormProps {
    htmlData: string | null;
    showHtml?: boolean;
    onZoomChange?: (zoom: { zoomValue: number }) => void;
    width: string;
}

const useStyles = makeStyles({
    scrollContainer: {
        overflow: 'auto',
        textAlign: 'center',
        height: '100%',
        overflowY: 'scroll',
        overflowX: 'scroll',
    },
    zoomWrapper: {
        display: 'inline-block',
        textAlign: 'left',
        verticalAlign: 'top',
    },
});

const DefaultHtmlViewer = React.forwardRef<IHtmlViewerRef, IHtmlViewerFormProps>(
    ({ htmlData, showHtml = false, onZoomChange, width }, ref) => {
        const styles = useStyles();
        const viewportRef = useRef<HTMLDivElement>(null);
        const wrapperRef = useRef<HTMLDivElement>(null);
        const contentRef = useRef<HTMLDivElement>(null);
        const onZoomChangeRef = useRef(onZoomChange);
        onZoomChangeRef.current = onZoomChange;
        const { formSettings } = useFormView();
        const formSettingsRef = useRef(formSettings);
        formSettingsRef.current = formSettings;

        const magnification = useMemo(
            () =>
                new HtmlMagnification(contentRef, wrapperRef, viewportRef, (zoom) => {
                    onZoomChangeRef.current?.({ zoomValue: zoom });
                }),
            []
        );

        useImperativeHandle(ref, () => ({ magnification }));

        const refreshFitZoomForLayout = () => {
            const fitType = magnification.getCurrentFitType();
            if (fitType === 'fitToWidth' || fitType === 'fitToPage') {
                magnification.refresh();
            }
        };

        useLayoutEffect(() => {
            if (!htmlData || !showHtml) {
                return;
            }

            applyFormZoomSettings(magnification, formSettingsRef.current);
            const zoomPercent = Math.round(magnification.getCurrentZoom() * 100);
            onZoomChangeRef.current?.({ zoomValue: zoomPercent });
        }, [htmlData, showHtml, magnification, width]);

        useEffect(() => {
            if (!htmlData || !showHtml || !contentRef.current) {
                return;
            }

            const contentResizeObserver = new ResizeObserver(() => {
                magnification.refresh();
            });

            contentResizeObserver.observe(contentRef.current);

            return () => {
                contentResizeObserver.disconnect();
            };
        }, [htmlData, showHtml, magnification]);

        useEffect(() => {
            if (!htmlData || !showHtml) {
                return;
            }

            const viewportResizeObserver = new ResizeObserver(() => {
                refreshFitZoomForLayout();
            });

            const elementsToObserve: Element[] = [];
            if (viewportRef.current) {
                elementsToObserve.push(viewportRef.current);
            }

            const mainContentContainer = document.getElementById('main-content-container');
            if (mainContentContainer) {
                elementsToObserve.push(mainContentContainer);
            }

            if (elementsToObserve.length === 0) {
                return;
            }

            elementsToObserve.forEach((element) => viewportResizeObserver.observe(element));

            return () => {
                viewportResizeObserver.disconnect();
            };
        }, [htmlData, showHtml, magnification, width]);

        return (
            <div ref={viewportRef} className={mergeClasses(styles.scrollContainer)} style={{ width: width || '100%' }}>
                <div ref={wrapperRef} className={styles.zoomWrapper}>
                    <div ref={contentRef} dangerouslySetInnerHTML={{ __html: SanitizeHtmlHelper.sanitize(htmlData ?? '') }} />
                </div>
            </div>
        );
    }
);

DefaultHtmlViewer.displayName = 'DefaultHtmlViewer';

export default DefaultHtmlViewer;
